/**
 * Section 3 — CUECs (Complementary User Entity Controls).
 *
 * One card per CUEC. Each card shows the CUEC text, AIP-6's applicability +
 * coverage judgment, a Cloudscape Multiselect for the user-org RCM mapping
 * (virtualScroll for large RCMs), and an inline A19 textarea when coverage
 * is partial or none.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SpaceBetween,
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  Alert,
  Spinner,
} from "@cloudscape-design/components";
import type { Cuec, RcmControl } from "@/core/api/types";
import { PdfPageLink } from "@/core/ui/PdfPageLink";
import { SectionStatusBadge } from "@/core/ui/SectionStatusBadge";
import type { Focus } from "@/shell/ReportDetail";
import { AddCuecModal } from "./AddCuecModal";
import { CuecCard } from "./CuecCard";
import { listCuecs, listRcmControls } from "./api";

interface Props {
  reportId: string;
  reportStatus: "uploading" | "processing" | "ready" | "error" | "awaiting_upload";
  openCount?: number;
  focus?: Focus | null;
  sectionPage?: number | null;
  /** #88: refresh ReportDetail's open count after a manual add/delete/save. */
  onItemsChanged?: () => void;
}

export function CuecsSection({
  reportId,
  reportStatus,
  openCount = 0,
  focus,
  sectionPage,
  onItemsChanged,
}: Props) {
  const [cuecs, setCuecs] = useState<Cuec[]>([]);
  const [rcm, setRcm] = useState<RcmControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, r] = await Promise.all([listCuecs(reportId), listRcmControls()]);
      setCuecs(c);
      setRcm(r);
      onItemsChanged?.(); // #88: refresh the report-level open count too
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [reportId, onItemsChanged]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (reportStatus !== "uploading" && reportStatus !== "processing") return;
    const id = setInterval(() => void refresh(), 5_000);
    return () => clearInterval(id);
  }, [reportStatus, refresh]);

  // #100: CUECs sit together on one page; per-CUEC pages drift, so use the
  // first CUEC's page (the lowest) for every CUEC's "View in PDF" link.
  const firstCuecPage = useMemo(() => {
    const pages = cuecs
      .map((c) => (c.ai_data as { page?: number } | null)?.page)
      .filter((p): p is number => typeof p === "number");
    return pages.length ? Math.min(...pages) : undefined;
  }, [cuecs]);

  const ingestRunning = reportStatus === "uploading" || reportStatus === "processing";
  const expandDefault = focus?.section === "cuecs";
  // #C9: section header shows the total CUEC count, e.g. "CUECs (12)".
  const sectionHeader = `CUECs (${cuecs.length})`;
  const badge = (
    <SectionStatusBadge processing={ingestRunning} openCount={openCount} />
  );
  const addButton = (
    <Box>
      <Button iconName="add-plus" onClick={() => setAddOpen(true)}>
        Add CUEC
      </Button>
    </Box>
  );
  const addModal = (
    <AddCuecModal reportId={reportId} visible={addOpen} onDismiss={() => setAddOpen(false)} onAdded={refresh} />
  );

  if (loading && !cuecs.length && !ingestRunning)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="CUECs"
        headerActions={badge}
      >
        <Box textAlign="center" padding="l">
          <Spinner /> Loading CUECs…
        </Box>
      </ExpandableSection>
    );

  if (error)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="CUECs"
        headerActions={badge}
      >
        <Alert type="error" header="Failed to load CUECs" action={<Button onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      </ExpandableSection>
    );

  if (ingestRunning && !cuecs.length)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="CUECs"
        headerActions={badge}
      >
        <Box textAlign="center" padding="l" color="text-body-secondary">
          <Spinner /> Ingest is still running. AIP-5 + AIP-6 run after Scope + Controls — this view
          auto-refreshes when ready.
        </Box>
      </ExpandableSection>
    );

  if (!cuecs.length)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText={sectionHeader}
        headerActions={badge}
      >
        <SpaceBetween size="s">
          {typeof sectionPage === "number" && (
            <Box>
              <PdfPageLink reportId={reportId} page={sectionPage} />
            </Box>
          )}
          <Box color="text-body-secondary">
            No CUECs were found in this report. Unusual for a SOC 1 Type II — verify manually, or add one.
          </Box>
          {addButton}
        </SpaceBetween>
        {addModal}
      </ExpandableSection>
    );

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded={expandDefault}
      headerText={sectionHeader}
      headerActions={badge}
    >
      <SpaceBetween size="s">
        <ColumnLayout columns={1}>
          {cuecs.map((c, i) => (
            <CuecCard
              key={c.id}
              cuec={c}
              index={i + 1}
              rcmControls={rcm}
              reportId={reportId}
              onUpdated={refresh}
              focusTargetId={focus?.targetId ?? null}
              pageOverride={firstCuecPage}
            />
          ))}
        </ColumnLayout>
        {addButton}
      </SpaceBetween>
      {addModal}
    </ExpandableSection>
  );
}
